import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Field, Select } from "@/components/common";
import { OTHER_MED } from "@/components/MedRequestDialog";

export function RotationMedRequestDialog({ assignment, onClose }) {
  const { user } = useApp();
  const nav = useNavigate();
  const [meds, setMeds] = useState([]);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  useEffect(() => {
    if (!assignment) return;
    setF({ medication: "", custom_medication: "", external_labs: false, notes: "" });
    setCreated(null);
    api.get("/med-requests/options").then((r) => setMeds(r.data.medications));
  }, [assignment]);
  const submit = async () => {
    if (!f.medication) return toast.error("Select the medication to request");
    if (f.medication === OTHER_MED && !f.custom_medication.trim()) return toast.error("Enter Medication Name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/med-requests", { ...f, rotation_assignment_id: assignment.id });
      setCreated(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!assignment} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="rotation-mr-dialog">
        <DialogHeader><DialogTitle className="text-[#20336F] dark:text-blue-200">Request Medication</DialogTitle></DialogHeader>
        {created ? (
          <div className="space-y-3 text-center" data-testid="rotation-mr-success">
            <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
            <div className="font-semibold text-[#20336F] dark:text-blue-200">Medication Request Created Successfully</div>
            <div className="text-xs text-[var(--muted-ink)]">Patient ID {created.patient_code} · {created.medication} · Pending pharmacy submission</div>
            <div className="flex justify-center gap-2">
              <button className="btn btn-ghost" onClick={onClose} data-testid="rotation-mr-close">Close</button>
              <button className="btn btn-primary" onClick={() => nav(`/medication-requests?open=${created.id}`)} data-testid="rotation-mr-view">View Medication Request</button>
            </div>
          </div>
        ) : (
          <div className="grid gap-3">
            <div className="grid grid-cols-3 gap-2 rounded-md bg-[var(--surface)] p-2.5 text-xs" data-testid="rotation-mr-context">
              <div><div className="text-[var(--muted-ink)]">Patient ID</div><b data-testid="rotation-mr-patient">{assignment?.athena_id}</b></div>
              <div><div className="text-[var(--muted-ink)]">Location</div><b>{assignment?.location_name}</b></div>
              <div><div className="text-[var(--muted-ink)]">Requested by</div><b>{user.name}</b></div>
            </div>
            <Field label="Medication Requested" required><Select value={f.medication} onChange={(v) => setF({ ...f, medication: v })} placeholder="Select medication" testid="rotation-mr-medication" options={[...meds, OTHER_MED]} /></Field>
            {f.medication === OTHER_MED && <Field label="Enter Medication Name" required><input className="inp" value={f.custom_medication} onChange={(e) => setF({ ...f, custom_medication: e.target.value })} data-testid="rotation-mr-custom" /></Field>}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.external_labs} onChange={(e) => setF({ ...f, external_labs: e.target.checked })} data-testid="rotation-mr-external-labs" />Patient has External Labs</label>
            <Field label="Notes"><textarea className="inp" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional" data-testid="rotation-mr-notes" /></Field>
            <div className="flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="rotation-mr-submit">{busy ? "Submitting…" : "Submit Medication Request"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
